import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { CloneTenantRequest, PasswordSchema, Tenant } from '@bliks-io/contracts';
import { useCloneTenant } from '@/pages/admin/api/mutations/useTenants';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Label,
  PasswordInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn
} from '@bliks-io/ui';
import { FormDialog } from '@/components/dialogs/FormDialog';
import {
  TenantIdentityFields,
  TenantIdentityFormValues,
  dateInputToIso,
  isoToDateInput
} from './components/TenantIdentityFields';

interface CloneTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Die Vorlage, wenn sie schon feststeht — der Weg über die Zeile eines
   * Arbeitsbereichs.
   */
  source?: Tenant;
  /**
   * Die Arbeitsbereiche zur Wahl, wenn die Vorlage noch nicht feststeht.
   *
   * Der Weg über „Demo erstellen": wer eine Demo will, denkt an die Demo und
   * nicht daran, erst die richtige Zeile zu finden. Dann fragt der Dialog als
   * Erstes, wovon kopiert wird.
   */
  candidates?: Tenant[];
}

/**
 * Form shape, not the contract shape: expiresAt holds a date-input string and
 * demoPassword stays optional here — the demo/password pairing lives in the
 * contract's discriminated union, built in toCloneRequest.
 */
type CloneTenantFormValues = TenantIdentityFormValues & {
  demoPassword?: string;
  /** Die Vorlage. Pflicht, solange sie nicht von aussen feststeht. */
  sourceId?: string;
};

function toCloneRequest(data: CloneTenantFormValues): CloneTenantRequest {
  const identity = {
    name: data.name,
    slug: data.slug,
    expiresAt: dateInputToIso(data.expiresAt),
    description: data.description?.trim() || undefined
  };
  return data.tenantType === 'demo'
    ? { ...identity, tenantType: 'demo', demoPassword: data.demoPassword ?? '' }
    : { ...identity, tenantType: data.tenantType };
}

export function CloneTenantDialog({
  open,
  onOpenChange,
  source,
  candidates = []
}: CloneTenantDialogProps) {
  const { t } = useTranslation();
  const cloneTenant = useCloneTenant();

  // Clones default to a demo tenant (the common case — spinning up an isolated
  // demo from a real org), regardless of the source's type.
  const defaultValues: CloneTenantFormValues = {
    name: source ? t('tenantAdmin.cloneNameDefault', { name: source.name }) : '',
    slug: source ? `${source.slug}-copy` : '',
    tenantType: 'demo',
    expiresAt: isoToDateInput(source?.expiresAt),
    description: source?.description || '',
    sourceId: source?._id ?? ''
  };

  const onSubmit = async (data: CloneTenantFormValues) => {
    const chosen = source ?? candidates.find((tenant) => tenant._id === data.sourceId);
    // Die Auswahl ist ein Pflichtfeld — ohne sie kommt der Submit nicht hierher.
    if (!chosen) return;

    await cloneTenant.mutateAsync({
      sourceId: chosen._id,
      data: toCloneRequest(data)
    });
    // 202: the copy runs detached, so there is nothing left to wait for here.
    // Progress and outcome are watched on the tenant list, which polls the
    // clone jobs. Everything that can fail synchronously — a taken slug above
    // all — still surfaces in this dialog's alert, next to the field the admin
    // has to change.
    toast.info(t('tenantAdmin.cloneStarted'));
    onOpenChange(false);
  };

  return (
    <FormDialog<CloneTenantFormValues>
      open={open}
      onOpenChange={onOpenChange}
      title={t(source ? 'tenantAdmin.cloneTenant' : 'tenantAdmin.createDemo')}
      description={t(
        source ? 'tenantAdmin.cloneDescription' : 'tenantAdmin.createDemoDescription',
        { name: source?.name }
      )}
      icon={Copy}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitLabel={t('tenantAdmin.cloneTenant')}
      cancelLabel={t('tenantAdmin.cancel')}
      // `demoPassword` is off the list on purpose: it only renders while the
      // demo type is selected, so its server error belongs in the root alert.
      fields={['name', 'slug', 'tenantType', 'expiresAt', 'description', 'sourceId']}
      size="md"
    >
      {({ form }) => {
        const isDemo = form.watch('tenantType') === 'demo';
        return (
          <div className="space-y-4">
            <Alert colorScheme="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {t(isDemo ? 'tenantAdmin.cloneWarningDemoTitle' : 'tenantAdmin.cloneWarningTitle')}
              </AlertTitle>
              <AlertDescription>
                {t(isDemo ? 'tenantAdmin.cloneWarningDemo' : 'tenantAdmin.cloneWarning')}
              </AlertDescription>
            </Alert>

            {!source && (
              <SourceField
                form={form}
                candidates={candidates}
                onPick={(picked) => {
                  // Name und Kürzel folgen der Vorlage, solange sie niemand
                  // selbst angefasst hat — wer erst wählt und dann umbenennt,
                  // soll seine Eingabe nicht durch die nächste Wahl verlieren.
                  if (!form.getFieldState('name').isDirty) {
                    form.setValue('name', t('tenantAdmin.cloneNameDefault', { name: picked.name }));
                  }
                  if (!form.getFieldState('slug').isDirty) {
                    form.setValue('slug', `${picked.slug}-copy`);
                  }
                }}
              />
            )}

            <TenantIdentityFields form={form} autoSlug />

            {isDemo && (
              <div className="space-y-2">
                <Label htmlFor="demoPassword">{t('tenantAdmin.demoPassword')}</Label>
                <PasswordInput
                  id="demoPassword"
                  autoComplete="new-password"
                  {...form.register('demoPassword', {
                    // Checked against form values: the rule survives unmounting
                    // (switching demo -> customer) and must not block then.
                    // PasswordSchema mirrors the Zitadel org policy, so a weak
                    // password fails here instead of deep in the import.
                    validate: (value, formValues) =>
                      formValues.tenantType !== 'demo' ||
                      PasswordSchema.safeParse(value).success ||
                      t('tenantAdmin.demoPasswordRequired')
                  })}
                  className={cn(form.formState.errors.demoPassword && 'border-destructive')}
                />
                {form.formState.errors.demoPassword && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.demoPassword.message}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">{t('tenantAdmin.demoPasswordHint')}</p>
              </div>
            )}
          </div>
        );
      }}
    </FormDialog>
  );
}

/**
 * Wovon kopiert wird.
 *
 * Ein Pflichtfeld des Formulars und kein eigener Schritt: react-hook-form
 * prüft es dann mit allem anderen, und die Meldung steht an der Auswahl statt
 * in einem Hinweis über dem Dialog.
 */
function SourceField({
  form,
  candidates,
  onPick
}: {
  form: UseFormReturn<CloneTenantFormValues>;
  candidates: Tenant[];
  onPick: (picked: Tenant) => void;
}) {
  const { t } = useTranslation();
  const registration = form.register('sourceId', {
    required: t('tenantAdmin.cloneSourceRequired')
  });
  const sourceId = form.watch('sourceId');

  return (
    <div className="space-y-2">
      <Label htmlFor="sourceId">{t('tenantAdmin.cloneSource')}</Label>
      <Select
        value={sourceId || undefined}
        onValueChange={(value) => {
          form.setValue('sourceId', value, { shouldDirty: true, shouldValidate: true });
          const picked = candidates.find((tenant) => tenant._id === value);
          if (picked) onPick(picked);
        }}
      >
        <SelectTrigger id="sourceId" ref={registration.ref}>
          <SelectValue placeholder={t('tenantAdmin.cloneSourcePlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          {candidates.map((tenant) => (
            <SelectItem key={tenant._id} value={tenant._id}>
              {tenant.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {form.formState.errors.sourceId && (
        <p className="text-sm text-destructive">{form.formState.errors.sourceId.message}</p>
      )}
      <p className="text-sm text-muted-foreground">{t('tenantAdmin.cloneSourceHint')}</p>
    </div>
  );
}
